import PriceType from '@/dossier/constants/PriceType';
import { parseComment } from '@/dossier/converters/commentConverters';
import { CheckInData } from '@/dossier/models/CheckInData';
import { PickedUpData } from '@/dossier/models/PickedUpData';
import { parseMediaFiles } from '@/general/converters/mediaFileConverters';
import ReferenceService from '@/general/services/ReferenceService';
import MediaTargetType from '@/shared/constants/MediaTargetType';
import Severity from '@/shared/constants/Severity';
import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { parseMoment } from '@/shared/converters/dateConverters';
import { parseTag, parseTags } from '@/shared/converters/tagConverters';
import User from '@/shared/models/User';
import { Url } from '@/shared/models/common';
import { minutesSinceMidnight } from '@/shared/utils/dateTime';
import { base64ImageAsUrl } from '@/shared/utils/image';
import { isPresent } from '@/shared/utils/typescript';
import ExecutionStatus from '../constants/ExecutionStatus';
import WarrantyStatus from '../constants/WarrantyStatus';
import Job, { CreateJobData, JobOrder, JobPrice, JobPriceApproval, UpdateExecutionStatusData } from '../models/Job';
import JobChecklistDetails from '../models/JobChecklistDetails';
import Diagnostic, { ComplaintDiagnostic } from '../models/diagnosis/Diagnostic';
import { parseDiagnostics } from './diagnosticConverters';
import { parseJobItems } from './jobItemConverters';
import { CheckInFormData } from '@/dossier/models/CheckInContext';

export function parseWarrantyStatus(priceType: WarrantyStatus | PriceType): WarrantyStatus {
  switch (priceType) {
    case PriceType.FIXED:
    case PriceType.FLOAT:
      return WarrantyStatus.NO_WARRANTY;
    default:
      return priceType as WarrantyStatus;
  }
}

export const ABSTRACT_JOB_SCHEMA = {
  id: 'id',
  description: {
    content: 'description',
    editable: 'isDescriptionEditable',
  },
  imageCount: 'mediaCountImages',
  videoCount: 'mediaCountVideo',
  attachmentCount: 'mediaCountAttachments',
  diagnosticCount: 'diagnosticsCount',
  tags: (data: any) => parseTags(data.tags),
  sequenceNumber: 'sequenceNr',
};

const SCHEMA = {
  ...ABSTRACT_JOB_SCHEMA,
  isDeleted: 'isDeleted',
  order: parseJobOrder,
  workStatus: 'workStatus',
  priceApproval: parsePriceApproval,
  price: parsePrice,
  warrantyStatus: (data: any) => parseWarrantyStatus(data.price?.type),
  executionStatus: 'executionStatus',
  needFollowUp: 'isFollowUpAppointmentRequired',
  followUpResolvedOn: (data: any) => parseMoment(data.followUpAppointmentResolvedOn),
  allowedMinutes: 'allowedDuration',
  comment: (data: any) => parseComment(data.comment),
  checklistDetails: (data: any) => parseCheckListDetails(data.checklistDetails),
  createdBy: (data: any) => ReferenceService.getUser(data.createdById),
  createdOn: (data: any) => parseMoment(data.createdOn),
  allowedDiagnosticDuration: 'allowedDiagnosticDuration',
  assignedTo: (data: any) => User.withId(data.assignedToId),
  pauseReason: (data: any) => parseTag(data.pauseReason, { isPause: true }),
  mediaFiles: (data: any) => parseMediaFiles(data.media, MediaTargetType.JOB),
  diagnostics: parseJobDiagnostics,
  items: (data: any) => parseJobItems(data.invoiceItems),
  severity: parseSeverity,
  linkedJob: (data: any) => parseJob(data.linkedJob),
};

const PRICE_SCHEMA = {
  amountWithoutVat: 'priceVATexcl',
  amountWithVat: 'priceVATincl',
  dmsAmountWithoutVat: 'invoiceTotalVATexcl',
  dmsAmountWithVat: 'invoiceTotalVATincl',
  vatRate: (data: any) => data.taxRate / 100,
};

const PRICE_APPROVAL_SCHEMA = {
  status: (data: any) => data?.status ?? data ?? null,
  approvedOn: (data: any) => parseMoment(data?.approvedOn),
  approvedBy: (data: any) => data?.approvedBy ? User.withFullName(data?.approvedBy) : null,
  registeredBy: (data: any) => User.withId(data?.registeredById),
};

const CHECKLIST_SCHEMA = {
  status: (data: any) => parseCheckListStatus(data.status),
  checklistDescription: 'checklistDescription',
  itemDescription: 'itemDescription',
};

const COMPLAINT_SCHEMA = {
  id: 'id',
  complaint: 'userInputString1',
  conditions: 'userInputString2',
};

const ORDER_SCHEMA = {
  status: (data: any) => data.orderStatus ?? data.order?.status,
  orderedOn: (data: any) => parseMoment(data.order?.orderedOn),
  orderedBy: (data: any) => User.withFullName(data.order?.orderedBy),
  registeredBy: (data: any) => User.withId(data.order?.registeredById),
  source: 'order.source',
  signature: (data: any) => parseSignature(data.order?.signature),
  hasSignature: (data: any) => data.hasOrderSignature ?? isPresent(data.order?.signature),
};

function parsePrice(data: any): JobPrice {
  return parseEntity(data.price, PRICE_SCHEMA, JobPrice) ?? new JobPrice();
}

function parsePriceApproval(data: any): JobPriceApproval | null {
  return parseEntity(data.priceApproval ?? data.priceApprovalStatus, PRICE_APPROVAL_SCHEMA, JobPriceApproval);
}

export function parseJob(data: any): Job | null {
  return parseEntity(data, SCHEMA, Job);
}

export function parseCheckListDetails(data: any): JobChecklistDetails | null {
  return parseEntity(data, CHECKLIST_SCHEMA, JobChecklistDetails);
}

function parseJobOrder(data: any): JobOrder | null {
  return parseEntity(data, ORDER_SCHEMA, JobOrder);
}

export function parseComplaintDiagnostic(data: any): ComplaintDiagnostic | null {
  return parseEntity(data, COMPLAINT_SCHEMA, ComplaintDiagnostic);
}

function parseJobDiagnostics(data: any): Array<Diagnostic> {
  const diagnostics = parseDiagnostics(data.diagnosticSteps);
  const complaint = parseComplaintDiagnostic(data.diagnosticComplaint);
  if (complaint && !diagnostics.some(d => d.id === complaint.id)) diagnostics.push(complaint);
  return diagnostics;
}

export function parseJobs(data: any): Array<Job> {
  return parseEntities(data, SCHEMA, Job);
}

export function parseSignature(data: any): Url | null {
  if (data && data.startsWith('https://')) return data;
  return base64ImageAsUrl(data);
}

export function parseSeverity(data: any): Severity | null {
  switch (data.severity) {
    case 'NOTICE':
      return Severity.GOOD;
    case 'ADVICE':
      return Severity.CAUTION;
    case 'URGENT':
      return Severity.BAD;
    default:
      return null;
  }
}

function parseCheckListStatus(data: any): Severity | null {
  return data === 99 ? null : data;
}

export function formatJobSeverity(severity: Severity | null): any {
  switch (severity) {
    case Severity.GOOD:
      return 'NOTICE';
    case Severity.CAUTION:
      return 'ADVICE';
    case Severity.BAD:
      return 'URGENT';
    default:
      return null;
  }
}

export function formatJobApproval(data: PickedUpData | CheckInData | CheckInFormData, jobs: Array<Job>, isCheckIn: boolean): any {
  return {
    approvalSource: data.signature ? 1 : 7,
    signature: data.signature,
    approvalBy: data.contact?.id ?? null,
    jobs: jobs.map(job => {
      return {
        id: job.id,
        approvalType: 'ORDER',
        status: job.order.status,
      };
    }),
    hasPriceApprovalPermission: 'canChangePriceAgreements' in data ? data.canChangePriceAgreements : undefined,
    isCheckIn,
    upsellingItemIds: 'selectedUpsellItemIds' in data ? data.selectedUpsellItemIds : [],
  };
}

export function formatJobData(data: CreateJobData): any {
  return {
    outletId: null,
    description: data.description,
    severity: formatJobSeverity(data.severity),
    executionStatus: data.executiveAgreement,
    orderStatus: data.orderStatus,
    price: null,
    isPartsRequested: false,
    isTechnicalInspectionIssue: false,
    repairOrderItemId: null,
    isDmsTransferable: false,
  };
}

export function formatJobPriceData(job: Job): any {
  let priceVATexcl = job.price.amountWithoutVat;
  let priceVATincl = job.price.amountWithVat;
  let type: WarrantyStatus | PriceType;

  if ([WarrantyStatus.WARRANTY, WarrantyStatus.AWAITING].includes(job.warrantyStatus) || !isPresent(job.price.amountWithoutVat)) {
    priceVATincl = null;
    priceVATexcl = null;
  }

  if (job.warrantyStatus !== WarrantyStatus.NO_WARRANTY) type = job.warrantyStatus;
  else if (job.price.amountWithoutVat === null) type = PriceType.FLOAT;
  else type = PriceType.FIXED;

  return {
    description: job.description.content,
    linkedJobId: job.linkedJob?.id ?? null,
    price: {
      type,
      priceVATexcl,
      taxRate: job.price.vatRate === null ? null : job.price.vatRate * 100, // Backend expects percentage, not decimal
      priceVATincl,
    },
  };
}

export function formatExecutionStatusForAdmin(data: UpdateExecutionStatusData): any {
  let executionStatus;

  if (data.status === ExecutionStatus.EXECUTE) {
    executionStatus = data.agree ? ExecutionStatus.EXECUTE : ExecutionStatus.DO_NOT_EXECUTE;
  } else {
    executionStatus = data.agree ? ExecutionStatus.EXECUTE_DIAGNOSIS : ExecutionStatus.DO_NOT_DIAGNOSE;
  }

  const durationInMinutes = data.duration ? minutesSinceMidnight(data.duration) : null;

  return {
    executionStatus,
    allowedDuration: (executionStatus === ExecutionStatus.EXECUTE && durationInMinutes) || null,
    allowedDiagnosticDuration: (executionStatus === ExecutionStatus.EXECUTE_DIAGNOSIS && durationInMinutes) || null,
  };
}

export function formatExecutionStatusForMechanic(data: UpdateExecutionStatusData): any {
  return data.jobs.map(j => ({
    jobId: j.id,
    executionStatus: data.status,
  }));
}

export function formatPriceApproval(job: Job): any {
  return {
    id: job.id,
    approvalType: 'PRICE',
    status: job.priceApproval.status,
  };
}

export function formatOrderApproval(job: Job): any {
  return {
    id: job.id,
    approvalType: 'ORDER',
    status: job.order.status,
  };
}